import { useState, useRef } from "react";
import Cookies from "js-cookie";
import getCurrentDirectoryTree from "../../../../services/Documents/Tree/getCurrentDirectoryTree";

export const useCreateTreeService = (caseId) => {
    const [isLoadingTree, setIsLoadingTree] = useState(false);
    const [items, setItems] = useState([]);
    const [expandedFolderTree, setExpandedFolderTree] = useState([null]);

    const folderCache = useRef(new Map());

    const hasDataInCache = (folderId) => {
        const key = `${caseId || 'null'}_${folderId || 'root'}`;
        return folderCache.current.has(key);
    };

    const getDataFromCache = (folderId) => {
        const key = `${caseId || 'null'}_${folderId || 'root'}`;
        return folderCache.current.get(key);
    };

    const saveToCache = (folderId, data) => {
        const key = `${caseId || 'null'}_${folderId || 'root'}`;
        folderCache.current.set(key, data);
    };

    return [
        isLoadingTree,
        items,
        setItems,
        expandedFolderTree,
        async function setTree() {
            setIsLoadingTree(true);
            try {
                const userData = JSON.parse(Cookies.get("accountData") || "{}");
                if (!userData._id) {
                    console.error("No user ID found in cookies");
                    return;
                }

                const tree = await getCurrentDirectoryTree(
                    userData._id,
                    null,
                    caseId
                );

                setItems(tree);
                saveToCache(null, tree); // Save root level data
            } catch (error) {
                console.error("Error setting tree:", error);
            } finally {
                setIsLoadingTree(false);
            }
        },

        async function updateTreeBranch(currentParentId, forceRefresh = false) {
            if (isLoadingTree) return;

            if (forceRefresh || !hasDataInCache(currentParentId)) {
                setIsLoadingTree(true);
                try {
                    const userData = JSON.parse(Cookies.get("accountData") || "{}");
                    if (!userData._id) {
                        console.error("No user ID found in cookies");
                        return;
                    }

                    const tree = await getCurrentDirectoryTree(
                        userData._id,
                        currentParentId,
                        caseId
                    );

                    if (forceRefresh) {
                        const key = `${caseId || 'null'}_${currentParentId || 'root'}`;
                        folderCache.current.delete(key);
                    }

                    saveToCache(currentParentId, tree);

                    setItems(prevItems => {
                        const filteredItems = forceRefresh
                            ? prevItems.filter(item => {
                                if (item.type === "folder") {
                                    return item.parentFolderId !== currentParentId;
                                } else {
                                    return item.folderId !== currentParentId;
                                }
                            })
                            : prevItems;

                        const existingIds = new Set(filteredItems.map(item => item._id));
                        const newItems = tree.filter(item => !existingIds.has(item._id));

                        return [...filteredItems, ...newItems];
                    });
                } catch (error) {
                    console.error("Error updating tree branch:", error);
                } finally {
                    setIsLoadingTree(false);
                }
            } else {
                const cachedData = getDataFromCache(currentParentId);

                setItems(prevItems => {
                    const existingIds = new Set(prevItems.map(item => item._id));
                    const newItems = cachedData.filter(item => !existingIds.has(item._id));

                    if (newItems.length === 0) return prevItems;
                    return [...prevItems, ...newItems];
                });
            }
        },

        function createToggleFolder(navigateToFolder, userId, currentParentId) {
            return async (folderId) => {
                if (hasDataInCache(folderId)) {
                    setExpandedFolderTree((prev) => [
                        ...prev,
                        ...(!prev.includes(folderId) ? [folderId] : []),
                    ]);

                    navigateToFolder(folderId);
                    return;
                }

                setIsLoadingTree(true);
                try {
                    const children = await getCurrentDirectoryTree(
                        userId,
                        folderId,
                        caseId
                    );

                    // Save to cache
                    saveToCache(folderId, children);

                    setItems((prev) => {
                        const existingItemIds = new Set(prev.map(item => item._id));
                        const newItems = children.filter(item => !existingItemIds.has(item._id));

                        return [...prev, ...newItems];
                    });

                    setExpandedFolderTree((prev) => [
                        ...prev,
                        ...(!prev.includes(folderId) ? [folderId] : []),
                    ]);

                    navigateToFolder(folderId);
                } catch (error) {
                    console.error("Error toggling folder:", error);
                } finally {
                    setIsLoadingTree(false);
                }
            }
        }
    ];
};